import { Check, Copy, X } from 'lucide-react';
import { useState } from 'react';
import type { BuilderConfig } from '~/lib/builder-types';
import type { TechSpecState } from '~/lib/tech-spec/types';
import { exportAsTechJson, exportAsTechPrompt } from '~/lib/tech-spec-export';

interface FeatureEntry {
  slug: string;
  name: string;
}

interface SpecLike {
  name: string;
  slug: string;
}

interface Props {
  spec: SpecLike;
  builderConfig: BuilderConfig | null;
  techState: TechSpecState;
  features: FeatureEntry[];
  onClose: () => void;
}

type ExportTab = 'markdown' | 'json';

export default function TechExportModal({
  spec,
  builderConfig,
  techState,
  features,
  onClose,
}: Props) {
  const [tab, setTab] = useState<ExportTab>('markdown');
  const [copied, setCopied] = useState(false);

  const markdownOutput = exportAsTechPrompt(
    spec,
    builderConfig,
    techState,
    features,
  );
  const jsonOutput = JSON.stringify(
    exportAsTechJson(spec, builderConfig, techState, features),
    null,
    2,
  );

  const output = tab === 'markdown' ? markdownOutput : jsonOutput;

  function handleCopy() {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Export Technical Spec
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Copy and paste into Cursor, Claude, or any AI code builder
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3">
          {(['markdown', 'json'] as ExportTab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                tab === t
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t === 'markdown' ? 'Markdown (AI prompt)' : 'JSON'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden px-5 py-3">
          <div className="relative h-full">
            <pre className="h-full overflow-auto text-xs font-mono bg-gray-50 border border-gray-200 rounded-xl p-4 text-gray-700 whitespace-pre-wrap">
              {output}
            </pre>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
          <p className="text-xs text-gray-400">
            {
              features.filter((f) =>
                builderConfig ? builderConfig.features[f.slug]?.enabled : true,
              ).length
            }{' '}
            features ·{' '}
            {tab === 'markdown' ? `${markdownOutput.length} chars` : 'JSON'}
          </p>
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy to clipboard
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
